import { AlertProps, SvgIconProps } from "@mui/material";
import { ReactJSXElement } from "@emotion/react/types/jsx-namespace";
import { Order } from "../class/Order";
import { Strategy, TimeFrame } from "../store/slices/strategySlice";
import {
  LanguageCode,
  LoadingScreenOptions,
  ResolutionString,
  ThemeName,
} from "../charting_library/charting_library";
import { ErrorCode } from "../class/Error";
import { TSubscriptionRateId } from "./types/subscription-types";

export interface BotOrder {
  amount: number;
  amountReplacedBySubstituteOrder: number;
  avgFillPrice: number;
  creationDate: string;
  currentOpenAmount: number;
  externalOrderId: string;
  fees: number;
  id: string;
  price: number;
  realizedPnl: number;
  side: "LONG" | "SHORT";
  status: string;
  type:
    | "OPEN"
    | "EXECUTED"
    | "PARTIALLY_FILLED"
    | "CANCELLED"
    | "REPLACING"
    | "CANCELLED_AND_REPLACED";
  updateDate: string;
}

export interface PropsAdronIcon extends SvgIconProps {
  size?: number;
  colorCustom?: string;
}

// export interface KlineBybit {
//   close: number;
//   confirm: boolean;
//   end: number;
//   high: string;
//   interval: number;
//   low: number;
//   open: number;
//   start: number;
//   timestamp: number;
//   turnover: number;
//   volume: number;
// }

export interface TradingViewLaunchProps {
  symbol: string;
  locale: LanguageCode;
  debug: boolean;
  autosize: boolean;
  interval: ResolutionString;
  theme: ThemeName;
  allow_symbol_change?: boolean;
  timezone: any;
  datafeed: any;
  loading_screen?: LoadingScreenOptions;
}

export enum TimeSlices {
  ONE_MINUTE = "ONE_MINUTE",
  FIVE_MINUTES = "FIVE_MINUTES",
  FIFTEEN_MINUTES = "FIFTEEN_MINUTES",
  THIRTY_MINUTES = "THIRTY_MINUTES",
  ONE_HOUR = "ONE_HOUR",
  TWO_HOURS = "TWO_HOURS",
  FOUR_HOURS = "FOUR_HOURS",
  SIX_HOURS = "SIX_HOURS",
  EIGHT_HOURS = "EIGHT_HOURS",
  TWELVE_HOURS = "TWELVE_HOURS",
  ONE_DAY = "ONE_DAY",
}

export enum TimeSlicesRender {
  ONE_MINUTE = "1m",
  FIVE_MINUTES = "5m",
  FIFTEEN_MINUTES = "15m",
  THIRTY_MINUTES = "30m",
  ONE_HOUR = "1h",
  TWO_HOURS = "2h",
  FOUR_HOURS = "4h",
  SIX_HOURS = "6h",
  EIGHT_HOURS = "8h",
  TWELVE_HOURS = "12h",
  ONE_DAY = "1d",
}

export type BotTimeframe = keyof typeof TimeSlicesRender;

export interface TimeFramePresets {
  Short: TimeFrame[];
  Mid: TimeFrame[];
  Long: TimeFrame[];
}

export enum Colors {
  Destructive = "#EF4444",
  Success = "#04D538",
}

export enum BotType {
  FUTURES_GRID = "Futures Grid Bot",
  SPOT_GRID = "SPOT Grid Bot",
  STRATEGY = "Auto strategy",
}

export enum PositionBehaviourEnum {
  REDUCE = "Reduce",
  BOTH = "Both",
  ACCUMULATE = "Accumulate",
}

export enum BaseCurrency {
  USDT = "USDT",
  BTC = "BTC",
  ETH = "ETH",
  USDC = "USDC",
}

export type AdronSeverity =
  | AlertProps["severity"]
  | "successProfile"
  | "infoUpdated"
  | "infoCopied";

export enum GridTypeEnum {
  Arithmetic = "ARITHMETIC",
  Geometric = "GEOMETRIC",
}

export enum TimePresetType {
  Short = "SHORT_TERM",
  Mid = "MID_TERM",
  Long = "LONG_TERM",
  Custom = "CUSTOM",
}

export type TimePreset = "SHORT_TERM" | "MID_TERM" | "LONG_TERM" | "CUSTOM";

export enum TimePresetRender {
  SHORT_TERM = "Short",
  MID_TERM = "Mid",
  LONG_TERM = "Long",
  CUSTOM = "Custom",
}

export enum DirectionEnum {
  Central = "Central",
  Long = "Long",
  Short = "Short",
}

export enum OrderSideEnum {
  Long = "Long",
  Short = "Short",
}

export type RangeStart = "range" | "start";
export type EntityType = "bot" | "strategy";

export interface Ticker {
  id: string;
  base: string;
  quote: string;
  orderStep: number;
  priceStep: number;
  rank?: number;
  minNotional: number;
}

export interface TickerResponse {
  other: Ticker[];
  ranked: Ticker[];
}

export interface TickerBinance {
  price: number;
  symbol: string;
}

export interface BotFilter {
  value: string;
  label: string;
  subLabel: string;
  sortType: string;
  sortIcon: string;
}

export interface BotTypeEntity {
  archivedTotal: number;
  activeTotal: number;
  name: string;
}

export interface BotTypeResponse {
  gridPnl: number;
  totalProfit: number;
  realizedPnl: number;
  currency: string;
  futuresGrid: BotTypeEntity;
  spotGrid?: BotTypeEntity;
}

export interface StrategyType {
  name: string;
  active: number;
  archived: number;
}

export interface StrategyTypeResponse {
  totalProfit: number;
  strategies: StrategyType[];
}

export interface JwtToken {
  jwt: string;
}

export interface UserSubscription {
  id: TSubscriptionRateId;
  name: string;
  shortName: string;
  description: string;
  price: number;
  priceCurrencyCode: string;
  period: number;
  maxInvestment: number;
  maxBots: number;
  maxStrategies: number;
  expirationDateTime: string;
}

export interface UserResponse {
  profileId: string;
  accounts: Account[];
  settings: UserSettings[];
  subscription: UserSubscription | null;
  username?: string;
  email?: string;
}

export interface UserSettings {
  name: string;
  value: string;
  format: string;
}

export interface Settings {
  name: string;
  value: boolean;
  format: string;
}

// export interface NotificationResponse {
// 	title: string,
// 	message: string,
// 	state: string,
// 	type: string,
// 	eventTime: number
// }

export type Direction = "Short" | "Central" | "Long";
export type GridType = "ARITHMETIC" | "GEOMETRIC";
export type OrderSide = "Long" | "Short";

export interface CustomGridParameters {
  orderSide: OrderSide;
  openPrice: number;
  closePrice: number;
  amount: number;
}

export interface OrderBuildSteps {
  orders: Order[];
  stepInBase: number;
  stepInQuote: number;
}

export enum SeverityDescription {
  RUNNING = "Running",
  UNSUCCESSFUL_START = "Start failure",
  OPENING = "Opening",
  ERROR = "Error",
  CLOSING = "Closing",
  WAITING_TO_BE_CLOSED = "Closing",
  NO_LOSS_CLOSING = "No loss closing",
  SHUTDOWN = "Shutdown",
}

export enum SeverityBotColors {
  RUNNING = "#04D538",
  UNSUCCESSFUL_START = "#EF4444",
  OPENING = "#A2FF00",
  ERROR = "#EF4444",
  CLOSING = "#A2FF00",
  WAITING_TO_BE_CLOSED = "#A2FF00",
  NO_LOSS_CLOSING = "#A2FF00",
  SHUTDOWN = "#3A1890",
}

export type BotSeverity =
  | "RUNNING"
  | "UNSUCCESSFUL_START"
  | "OPENING"
  | "ERROR"
  | "CLOSING"
  | "NO_LOSS_CLOSING"
  | "SHUTDOWN"
  | "WAITING_TO_BE_CLOSED"
  | undefined;

export type Currency = "BASE" | "QUOTE";

export enum Platform {
  web = "WEB",
  web_mobile = "WEB_MOBILE",
  telegram_mini_app = "TELEGRAM_MINI_APP",
  android = "ANDROID",
  ios = "IOS",
}

export interface StrategyCreate {
  base: string;
  quote: string;
  accountId: string;
  profileId: string;
  marketType: string;
  currencyType: string;
  investment: number;
  investmentQuote: number;
  requiredMargin: number;
  minPrice: number;
  maxPrice: number;
  leverage: number;
  gridDirection: string;
  timeframes: TimeFrame[];
  presetType: TimePresetType;
  positionBehavior: string;
}

export interface CustomGridOrder {
  orderSide: string;
  openPrice: number;
  closePrice: number;
  amount: number;
  settingId?: string;
  doNotSubstitute?: boolean;
}

export interface BotCreate {
  base: string;
  quote: string;
  minPrice: number;
  maxPrice: number;
  botType: string;
  startPrice: number;
  initialGrids: number;
  initialAmount: number;
  initialAmountQuote: number;
  currencyType: Currency;
  gridDirection: string;
  leverage: number;
  gridType: GridType;
  priceStep: number;
  priceStepPercentage: string;
  accountId: string;
  isAutoManaged: boolean;
  orderAmount: number;
  closingAtInitialPrice: boolean;
  customGridSettings?: {};
}

export interface Bot {
  id: string;
  displayedId: string;
  accountName: string;
  quote: string;
  base: string;
  direction: string;
  leverage: number;
  investment: number;
  pnl: number;
  totalProfit: number; // GridProfit - Profit from orders
  pnlPercentage: number;
  total?: number;
  roi: number;
  runtime: number;
  stepsCount: number;
  minPrice: number;
  maxPrice: number;
  currencyType: Currency;
  grids: number;
  gridStepType: string;
  profitPerGrid: number;
  priceStepPrecision: number;
  initialAmountQuote: number;
  status: BotSeverity;
  gridDirection: Direction;
  startPrice: number;
  botType: string;
  totalInvestment: number; // юзер задает ползунком через ШАГ
  createdAt: string; // время создание
  accountId: string; // easy
  gridsAmount: number; // по умолчанию задаем 10
  gridType: "ARITHMETIC" | "GEOMETRIC";
  priceStep: number; // (maxPrice - minPrice) / grids amount (в BTC/ETH)
  gridsCompleted: number; // сколько прошел сеток бот (отображаем на card G grids completed)
  allGridsProfit: number; // ??? общая прибыль со всех сеток текущего бота
  totalFees: number; // нет на карточке
  unrealizedPnl: number;
  realizedProfit: number;
  apr: number; // нет на карточке
  isAutoManaged: boolean; // deprecated
  currentPosition: string; //
  // на какую сумму сейчас открыт ордер, ЗАПРОСИТЬ У ВАНЬКА параметры ниже
  currentPositionQuote: number;
  currentPositionBase: number;
  startedAt: string; // ВРЕМЯ СТАРТА ЗАПУСКА БОТА (статика из бэка), время запуска бота
  outOfRange: boolean;
  margin: number;
  priceStepQuote: number;
  priceStepPercentage: string;
  // minPrice: number,
  // maxPrice: number,
  closeTime: string;
  closedAt: string; // когда закрыли бота (для archived)
  ticker: Ticker;
  customGridSettings: CustomGridOrder[];
  avgPositionOpeningPrice: number;
  errorCode: ErrorCode | undefined;
  timeframe: BotTimeframe | undefined;
  customLowPrice: number;
  customHighPrice: number;
  orderAmount: number;
}

export interface BotPageResponse {
  page: number;
  size: number;
  sort: string;
}

export interface BotResponse {
  bots: Bot[];
  page: BotPageResponse;
  size: number;
  sort: string[];
}

export interface StrategiesResponse {
  strategies: Strategy[];
  page: number;
  size: number;
  totalPages: number;
  sort: string[];
}

export interface CryptoIcon {
  key: string;
  icon: Element;
}

export interface User {
  accounts: any;
  sub: string;
  name: string;
  iat: number;
  userid: string;
}

export interface TelegramUser {
  id: number;
  first_name: string;
  last_name: string;
  username: string;
  language_code: string;
  is_premium: boolean; // has premium
  allows_write_to_pm: boolean; // can send/write messages
}

export interface UserState {
  profile: UserResponse;
  telegramUser: TelegramUser | null;
  activeAccount: Account;
  newNotification: boolean;
  canStartBot: boolean;
  token: string | null;
}

export interface Settings {
  name: string;
  value: boolean;
  format: string;
}

export interface Account {
  id: string;
  name: string;
  market: Market;
  marginType: string;
  leverage: number;
  linkDateTime: string | null;
}

export interface AccountDetailInfo {
  id: string;
  name: string;
  profileId: string;
  market: Market;
  marginType: string;
  leverage: number;
  linkDatetime: string;
}

export interface MarketView {
  name: Market;
  displayedName: string;
}

export type Market = "BINANCE" | "AEVO" | "BYBIT" | "OKX" | "REYA-DEX" | "REYA_DEX" | "";

export interface MarketConnect {
  name: string;
  leverage: number;
  market?: Market;
  marketSecurityProps: MarketSecurityProps;
}

export interface MarketEdit {
  name: string;
  leverage: number;
  market?: Market;
}

export interface MarketSecurityProps {
  apiKey?: string;
  apiSecret?: string;
  passPhrase?: string;
  aevoKey?: string;
  aevoSecret?: string;
  privateKey?: string;
  signingKey?: string;
}

export interface Notification {
  message: string;
  description: string;
  state: string;
  type: "WARN" | "INFO" | "ERROR";
  eventTime: number;
}

export interface NotificationResponse {
  content: Notification[];
}

export interface NotificationStreaming {
  data: string;
  id: string;
  event: string;
  retry: number;
}

export type NotificationCategory = "ALL" | "INFO" | "WARNING" | "ERROR";

export interface NotificationTab {
  tab: NotificationCategory;
  notifications: Notification[];
}

export type TabIcon = {
  color: string;
  icon: ReactJSXElement;
};

export type TabIcons = {
  INFO: TabIcon;
  ERRORS: TabIcon;
  ALERTS: TabIcon;
  SUCCESS: TabIcon;
};

export interface BinancePriceResponse {
  symbol: string;
  price: string; // Цена возвращается в виде строки
}

export interface TelegramUser {
  id: number;
  first_name: string;
  last_name: string;
  username: string;
  language_code: string;
}

export interface WebApp {
  initData: string;
  initDataUnsafe: {
    query_id: string;
    user: TelegramUser;
    auth_date: string;
    hash: string;
  };
  version: string;
  platform: string;
  colorScheme: string;
  themeParams: {
    link_color: string;
    button_color: string;
    button_text_color: string;
    secondary_bg_color: string;
    hint_color: string;
    bg_color: string;
    text_color: string;
  };
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  isClosingConfirmationEnabled: boolean;
  headerColor: string;
  backgroundColor: string;
  BackButton: {
    isVisible: boolean;
  };
  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isProgressVisible: boolean;
    isActive: boolean;
  };
  HapticFeedback: any;
}
